import { ChangeDetectionStrategy, Component, computed, inject, Injectable, input, model } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { bzIconCalendarOutline, bzIconClose } from '@bringoz/ui/icons';
import { BzIcon } from '../icon/bz-icon';

/**
 * A calendar day, `YYYY-MM-DD`.
 *
 * A day rather than a `Date` or a timestamp, because a day has no time and no
 * timezone: `2026-10-05` is the 5th everywhere. A `Date` is an instant, and an
 * instant is a different calendar day depending on where it is read — which is
 * the classic off-by-one when a site's timezone is not the browser's. Turning a
 * day into the instant the application stores (start of day in the site's
 * timezone, say) is the application's business, done at its own boundary.
 *
 * It also keeps `moment` out of this library's public API — see the date-type
 * decision in docs/component-roadmap.md, Tier 3.
 */
export type BzDay = string;

/** `sm` is the compact 30px field, `md` the default 40px one — the same pair as `bz-select`. */
export type BzDatePickerSize = 'sm' | 'md';

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `'2026-10-05'` → a local-midnight `Date`, or null for anything that is not a day. */
export function bzDayToDate(day: BzDay | null | undefined): Date | null {
  const match = day ? DAY_PATTERN.exec(day) : null;
  return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null;
}

/** A `Date` → its local calendar day. The time of day is dropped. */
export function bzDateToDay(date: Date | null | undefined): BzDay | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Identity token for "the text in the field". Material asks the adapter to
 * format a date with one of the objects in `MAT_DATE_FORMATS`; this one is
 * recognised by reference and rendered with the component's `format` pattern
 * instead of `Intl`. Every other label (month header, accessible names) still
 * goes through `Intl`, so they stay localised.
 */
const FIELD_FORMAT = { bzField: true };

const FORMATS: MatDateFormats = {
  parse: { dateInput: FIELD_FORMAT },
  display: {
    dateInput: FIELD_FORMAT,
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

/**
 * Material's native adapter with one change: the field text follows a
 * `MM/DD/YYYY`-style pattern, which is how every Bringoz application stores
 * its site date format. `Intl` cannot express an arbitrary pattern.
 *
 * Provided per component instance, so each picker can carry its own pattern.
 */
@Injectable()
class BzDateAdapter extends NativeDateAdapter {
  // Functions rather than values: they read the component's inputs at the moment Material asks,
  // so a changed input is never one step behind.
  pattern: () => string = () => 'MM/DD/YYYY';
  firstDayOfWeek: () => number = () => 0;

  // Not from the locale: the console's calendars start on Sunday in every language, and a
  // picker that followed the browser locale would disagree with the date range pickers next to it.
  override getFirstDayOfWeek(): number {
    return this.firstDayOfWeek();
  }

  override format(date: Date, displayFormat: object): string {
    if (displayFormat !== FIELD_FORMAT) {
      return super.format(date, displayFormat);
    }
    const pad = (n: number): string => String(n).padStart(2, '0');
    const parts: Record<string, string> = {
      YYYY: String(date.getFullYear()),
      MM: pad(date.getMonth() + 1),
      DD: pad(date.getDate()),
    };
    return this.pattern().replace(/YYYY|MM|DD/g, (token) => parts[token]);
  }
}

/**
 * A date field that opens a calendar. Built on `MatDatepicker`, which already
 * gets the expensive parts right — the keyboard model of the calendar grid,
 * month and year views, min/max and a day filter, focus returning to the
 * field — and is themed onto Bringoz tokens by styles/material.css.
 *
 * The field is read-only: a day is picked in the calendar, never typed. That is
 * how the console's pickers already behave, and it removes a whole class of
 * parse errors (is `03/04` March or April?).
 */
@Component({
  selector: 'bz-date-picker',
  imports: [MatFormFieldModule, MatInputModule, MatDatepickerModule, BzIcon],
  templateUrl: './bz-date-picker.html',
  styleUrl: './bz-date-picker.scss',
  providers: [
    { provide: DateAdapter, useClass: BzDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FORMATS },
  ],
  // `size` reaches the host for the same reason as in bz-select: the field's
  // height is set through Material's custom properties on an ancestor.
  host: {
    '[class.bz-date-picker--sm]': "size() === 'sm'",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzDatePicker {
  /** The picked day, or null. */
  readonly value = model<BzDay | null>(null);
  readonly disabled = model(false);

  /** Earliest day that can be picked. */
  readonly min = input<BzDay | null>(null);
  /** Latest day that can be picked. */
  readonly max = input<BzDay | null>(null);
  /** Return false to make a day unpickable — weekends, holidays, days without data. */
  readonly filter = input<((day: BzDay) => boolean) | null>(null);

  /** Floating label above the field. Leave unset for a bare control. */
  readonly label = input('');
  readonly placeholder = input('');
  readonly size = input<BzDatePickerSize>('md');
  /** How the day is written in the field: `YYYY`, `MM` and `DD` with any separators. */
  readonly format = input('MM/DD/YYYY');
  /** 0 = Sunday … 6 = Saturday. Sunday by default, as everywhere in the console. */
  readonly firstDayOfWeek = input(0);
  /** Shows an × that empties the field. */
  readonly clearable = input(false);

  /** Accessible name. Unnecessary when `label` is set. */
  readonly ariaLabel = input('');
  readonly clearLabel = input('Clear date');
  readonly openLabel = input('Open calendar');

  protected readonly calendarIcon = bzIconCalendarOutline;
  protected readonly clearIcon = bzIconClose;

  // Reads `format` so that a new pattern hands the field a new Date: MatDatepickerInput only
  // re-renders its text when its value is set, and Angular only sets it when the reference changes.
  protected readonly date = computed(() => {
    this.format();
    return bzDayToDate(this.value());
  });
  protected readonly minDate = computed(() => bzDayToDate(this.min()));
  protected readonly maxDate = computed(() => bzDayToDate(this.max()));
  protected readonly dateFilter = computed(() => {
    const filter = this.filter();
    return (date: Date | null): boolean => !filter || !date || filter(bzDateToDay(date)!);
  });

  constructor() {
    const adapter = inject(DateAdapter) as BzDateAdapter;
    adapter.pattern = () => this.format();
    adapter.firstDayOfWeek = () => this.firstDayOfWeek();
  }

  protected onDateChange(date: Date | null): void {
    this.value.set(bzDateToDay(date));
  }

  protected clear(event: MouseEvent): void {
    // The × sits inside the field, whose click opens the calendar.
    event.stopPropagation();
    this.value.set(null);
  }
}
